import React, { FC, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Form, InputNumber, notification, Row, Select, Typography, Upload } from "antd";
import { UploadChangeParam } from "antd/lib/upload/interface";

import ContentTitle from "../../../components/ContentTitle/ContentTitle";
import FormInput from "../../../components/FormInput/FormInput";
import { selectProduct } from "../../../redux-toolkit/product/product-selector";
import {
    selectAdminStateErrors,
    selectIsAdminStateLoading,
    selectIsProductEdited
} from "../../../redux-toolkit/admin/admin-selector";
import { LoadingStatus } from "../../../types/types";
import { resetAdminState, setAdminLoadingState } from "../../../redux-toolkit/admin/admin-slice";
import { fetchProduct } from "../../../redux-toolkit/product/product-thunks";
import IconButton from "../../../components/IconButton/IconButton";
import EditProductSelect from "./EditProductSelect";
import { updateProduct } from "../../../redux-toolkit/admin/admin-thunks";
import { fetchCategories } from "../../../redux-toolkit/category/category-thunks";
import { selectCategories } from "../../../redux-toolkit/category/category-selector";
import "./EditProduct.css";

type EditProductData = {
    productTitle: string;
    productTitleEn: string;
    vendor: string;
    year: string;
    country: string;
    type: string;
    length: number;
    width: number;
    height: number;
    diameter: number;
    gender: string;
    categoryId: number;
    topDescription: string;
    topDescriptionEn: string;
    middleDescription: string;
    baseDescription: string;
    baseDescriptionEn: string;
    price: string;
};

const EditProduct: FC = (): ReactElement => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const params = useParams<{ id: string }>();
    const productData = useSelector(selectProduct);
    const isLoading = useSelector(selectIsAdminStateLoading);
    const errors = useSelector(selectAdminStateErrors);
    const isProductEdited = useSelector(selectIsProductEdited);
    const categories = useSelector(selectCategories);
    const [fileList, setFileList] = React.useState<any[]>([]);

    useEffect(() => {
        dispatch(setAdminLoadingState(LoadingStatus.LOADED));
        dispatch(fetchProduct(params.id));
        dispatch(fetchCategories());

        return () => {
            dispatch(resetAdminState(LoadingStatus.LOADING));
        };
    }, []);

    useEffect(() => {
        if (productData) {
            form.setFieldsValue(productData);
            // productData.category là TÊN danh mục -> tìm id tương ứng để chọn sẵn.
            const matched = categories.find((category) => category.name === productData.category);
            if (matched) {
                form.setFieldsValue({ categoryId: matched.id });
            }
        }
    }, [productData, categories])

    useEffect(() => {
        if (isProductEdited) {
            window.scrollTo(0, 0);
            notification.success({
                message: "Đã cập nhật sản phẩm",
                description: "Cập nhật sản phẩm thành công!"
            });
            dispatch(resetAdminState(LoadingStatus.SUCCESS));
        }
    }, [isProductEdited]);

    const onFormSubmit = (data: EditProductData): void => {
        const bodyFormData: FormData = new FormData();
        fileList.forEach((item) => {
            const raw = item.originFileObj || item;
            if (raw) {
                bodyFormData.append("files", raw);
            }
        });
        bodyFormData.append(
            "product",
            new Blob([JSON.stringify({ ...data, id: productData?.id })], { type: "application/json" })
        );

        dispatch(updateProduct(bodyFormData));
    };

    const handleUpload = ({ fileList: newFileList }: UploadChangeParam<any>): void => {
        setFileList(newFileList.slice(0, 5));
    };

    return (
        <div>
            <ContentTitle title={"Sửa sản phẩm"} titleLevel={4} icon={<EditOutlined />} />
            <Form onFinish={onFormSubmit} form={form}>
                <Row gutter={32}>
                    <Col xs={24} md={12}>
                        <FormInput
                            title={"Tên sản phẩm"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"productTitle"}
                            error={errors.productTitleError}
                            disabled={isLoading}
                            placeholder={"Tên sản phẩm"}
                        />
                        <FormInput
                            title={"Tên sản phẩm (English)"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"productTitleEn"}
                            disabled={isLoading}
                            placeholder={"Tùy chọn - để trống sẽ hiện tên tiếng Việt"}
                        />
                        <FormInput
                            title={"Thương hiệu"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"vendor"}
                            error={errors.vendorError}
                            disabled={isLoading}
                            placeholder={"Thương hiệu"}
                        />
                        <FormInput
                            title={"Năm sản xuất"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"year"}
                            error={errors.yearError}
                            disabled={isLoading}
                            placeholder={"Năm sản xuất"}
                        />
                        <FormInput
                            title={"Xuất xứ"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"country"}
                            error={errors.countryError}
                            disabled={isLoading}
                            placeholder={"Xuất xứ"}
                        />
                        <EditProductSelect
                            title={"Loại sản phẩm"}
                            name={"type"}
                            placeholder={"Loại sản phẩm"}
                            error={errors.typeError}
                            disabled={isLoading}
                            values={["Handmade", "Home decor", "Kitchenware", "Accessory"]}
                        />
                        <EditProductSelect
                            title={"Đối tượng"}
                            name={"gender"}
                            placeholder={"Đối tượng"}
                            disabled={isLoading}
                            values={["all", "male", "female", "unisex"]}
                        />
                        <Row className={"form-item"}>
                            <Col span={6}>
                                <Typography.Text>Danh mục</Typography.Text>
                            </Col>
                            <Col span={18}>
                                <Form.Item name={"categoryId"}>
                                    <Select
                                        placeholder={"Chọn danh mục"}
                                        disabled={isLoading}
                                        style={{ width: "100%" }}
                                    >
                                        {categories.map((category) => (
                                            <Select.Option key={category.id} value={category.id}>
                                                {category.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className={"form-item"} gutter={8}>
                            <Col span={6}>
                                <Typography.Text>Dài (cm)</Typography.Text>
                                <Form.Item name={"length"} initialValue={0}>
                                    <InputNumber min={0} style={{ width: "100%" }} disabled={isLoading} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Typography.Text>Rộng (cm)</Typography.Text>
                                <Form.Item name={"width"} initialValue={0}>
                                    <InputNumber min={0} style={{ width: "100%" }} disabled={isLoading} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Typography.Text>Cao (cm)</Typography.Text>
                                <Form.Item name={"height"} initialValue={0}>
                                    <InputNumber min={0} style={{ width: "100%" }} disabled={isLoading} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Typography.Text>Đường kính (cm)</Typography.Text>
                                <Form.Item name={"diameter"} initialValue={0}>
                                    <InputNumber min={0} style={{ width: "100%" }} disabled={isLoading} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <FormInput
                            title={"Mô tả ngắn"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"topDescription"}
                            error={errors.topDescriptionError}
                            disabled={isLoading}
                            placeholder={"Mô tả ngắn"}
                        />
                        <FormInput
                            title={"Mô tả ngắn (English)"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"topDescriptionEn"}
                            disabled={isLoading}
                            placeholder={"Tùy chọn - để trống sẽ hiện tiếng Việt"}
                        />
                        <FormInput
                            title={"Chi tiết nổi bật"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"middleDescription"}
                            error={errors.middleDescriptionError}
                            disabled={isLoading}
                            placeholder={"Chi tiết nổi bật"}
                        />
                        <FormInput
                            title={"Chất liệu / bảo quản"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"baseDescription"}
                            error={errors.baseDescriptionError}
                            disabled={isLoading}
                            placeholder={"Chất liệu / bảo quản"}
                        />
                        <FormInput
                            title={"Chất liệu / bảo quản (English)"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"baseDescriptionEn"}
                            disabled={isLoading}
                            placeholder={"Tùy chọn - để trống sẽ hiện tiếng Việt"}
                        />
                        <FormInput
                            title={"Giá"}
                            titleSpan={6}
                            wrapperSpan={18}
                            name={"price"}
                            error={errors.priceError}
                            disabled={isLoading}
                            placeholder={"Giá (đơn vị nghìn đồng)"}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Upload
                            name={"files"}
                            multiple
                            maxCount={5}
                            listType={"picture"}
                            accept={"image/*"}
                            fileList={fileList}
                            onChange={handleUpload}
                            beforeUpload={() => false}
                        >
                            <Button icon={<UploadOutlined />} disabled={fileList.length >= 5}>
                                Chọn ảnh (tối đa 5)
                            </Button>
                        </Upload>
                        <p className="mt-sm font-body-md text-[13px] text-on-surface-variant">
                            {fileList.length > 0
                                ? "Ảnh mới sẽ thay thế toàn bộ ảnh hiện tại khi lưu."
                                : "Ảnh hiện tại (để trống nếu không muốn thay đổi):"}
                        </p>
                        {fileList.length === 0 && (
                            <div className="mt-xs flex flex-wrap gap-xs">
                                {(productData.images && productData.images.length > 0
                                    ? productData.images
                                    : productData.filename
                                    ? [productData.filename]
                                    : []
                                ).map((src, index) => (
                                    <img
                                        key={index}
                                        src={src}
                                        alt={`${productData.productTitle} ${index + 1}`}
                                        className="h-24 w-24 rounded-lg border border-outline-variant/50 object-cover"
                                    />
                                ))}
                            </div>
                        )}
                    </Col>
                </Row>
                <IconButton title={"Lưu thay đổi"} icon={<EditOutlined />} disabled={isLoading} />
            </Form>
        </div>
    );
};

export default EditProduct;
